import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Input, Select, message } from 'antd';
import { SnippetsOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { superadminInvoicesApi, companiesApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatApiDateTimeJalali } from '../../utils/dateUtils';
import { formatMoneyFa } from '../../utils/formatPriceToman';

const { Option } = Select;

const PACKAGE_TYPE_LABEL = {
  monthly: 'ماهانه',
  annual: 'سالانه',
  lifetime: 'مادام‌العمر',
};

const fmtDt = (v) => formatApiDateTimeJalali(v) || '—';

const SuperadminInvoicesSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [companyId, setCompanyId] = useState('all');
  const [search, setSearch] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await companiesApi.list({ page: 1, per_page: 500 });
      setCompanyOptions(res?.data?.data || []);
    } catch {
      setCompanyOptions([]);
    }
  }, []);

  const fetchList = useCallback(
    async (page = 1, perPage = 10) => {
      setLoading(true);
      try {
        const params = {
          company_id: companyId,
          search: search.trim() || undefined,
          page,
          per_page: perPage,
        };
        const res = await superadminInvoicesApi.list(params);
        const data = res?.data || {};
        setRows(data.data || []);
        setPagination({
          current: Number(data.current_page || page),
          pageSize: Number(data.per_page || perPage),
          total: Number(data.total || 0),
        });
      } catch (error) {
        message.error(error?.message || 'خطا در دریافت صورتحساب‌ها');
      } finally {
        setLoading(false);
      }
    },
    [companyId, search],
  );

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    fetchList(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- همان الگوی ایندکس سفارشات
  }, [companyId, search]);

  const columns = useMemo(
    () => [
      {
        title: 'شماره',
        dataIndex: 'invoice_number',
        key: 'invoice_number',
        width: 88,
        fixed: 'left',
      },
      {
        title: 'شرکت',
        key: 'company',
        ellipsis: true,
        responsive: ['md'],
        render: (_, r) => r.company?.company_name || '—',
      },
      {
        title: 'بسته',
        key: 'package',
        ellipsis: true,
        render: (_, r) => r.package?.name || '—',
      },
      {
        title: 'نوع',
        dataIndex: 'package_type',
        key: 'package_type',
        width: 100,
        render: (t) => PACKAGE_TYPE_LABEL[t] || t || '—',
      },
      {
        title: 'مبلغ',
        key: 'total',
        width: 120,
        align: 'left',
        render: (_, r) => {
          const sym = r.currency?.currency_symbol || '';
          const n = r.total;
          if (n == null) return '—';
          return formatMoneyFa(n, sym);
        },
      },
      {
        title: 'درگاه',
        dataIndex: 'gateway_name',
        key: 'gateway_name',
        width: 100,
        ellipsis: true,
        render: (t) => (t && t !== '-' ? t : '—'),
      },
      {
        title: 'شماره تراکنش',
        dataIndex: 'transaction_id',
        key: 'transaction_id',
        ellipsis: true,
        responsive: ['lg'],
        render: (t) => t || '—',
      },
      {
        title: 'تاریخ پرداخت',
        dataIndex: 'pay_date',
        key: 'pay_date',
        ellipsis: true,
        responsive: ['xl'],
        render: fmtDt,
      },
      {
        title: 'پرداخت بعدی',
        dataIndex: 'next_pay_date',
        key: 'next_pay_date',
        ellipsis: true,
        responsive: ['xl'],
        render: fmtDt,
      },
    ],
    [],
  );

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-900/25 flex-shrink-0">
                <SnippetsOutlined className="text-lg sm:text-xl text-amber-600 dark:text-amber-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">صورتحساب</h1>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm m-0 truncate">فهرست صورتحساب‌های اشتراک شرکت‌ها</p>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchList(pagination.current, pagination.pageSize)}>
              بروزرسانی
            </Button>
          </div>

          <div className="bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Select
                value={companyId}
                onChange={setCompanyId}
                showSearch
                optionFilterProp="children"
                popupClassName="custom-font"
                placeholder="همه شرکت‌ها"
                style={{ minWidth: 220, width: '100%', maxWidth: 320, fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه شرکت‌ها</Option>
                {companyOptions.map((c) => (
                  <Option key={c.id} value={String(c.id)}>
                    {c.company_name}
                  </Option>
                ))}
              </Select>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجو در صورتحساب..."
                prefix={<SearchOutlined className="text-gray-400" />}
                className="rounded-lg"
                style={{ fontFamily: 'CustomFont', maxWidth: 360, width: '100%', minWidth: 200 }}
                size="middle"
              />
            </div>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              {loading ? (
                <div className="p-4 sm:p-5">
                  <TableSkeleton rows={8} columns={6} />
                </div>
              ) : (
                <Table
                  rowKey="id"
                  loading={false}
                  columns={columns}
                  dataSource={rows}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    responsive: true,
                    onChange: (page, pageSize) => fetchList(page, pageSize),
                  }}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminInvoicesSettings;
